import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Phone, MapPin, Star, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { SectionDecor } from "@/components/SectionDecor";
import { ServiceTile } from "@/components/ServiceTile";
import { Button } from "@/components/ui/button";
import { caCities, company } from "@/data/site";
import { cityAreaContent } from "@/data/cityAreaContent";
import { getCitySectionExtra } from "@/data/citySectionExtras";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";
import areasHero from "@/assets/heroes/areas-served.jpg";
import heroRooftops from "@/assets/heroes/areas-rooftops.jpg";
import sectionImg1 from "@/assets/mckay/roof-repair.webp.asset.json";
import sectionImg2 from "@/assets/mckay/roof-replacement.webp.asset.json";
import sectionImg3 from "@/assets/mckay/solar-install.webp.asset.json";
import sanDiegoHero from "@/assets/city-heroes/san-diego.jpg";
import nationalCityHero from "@/assets/city-heroes/national-city.jpg";
import springValleyHero from "@/assets/city-heroes/spring-valley.jpg";
import laMesaHero from "@/assets/city-heroes/la-mesa.jpg";
import elCajonHero from "@/assets/city-heroes/el-cajon.jpg";
import lemonGroveHero from "@/assets/city-heroes/lemon-grove.jpg";
import chulaVistaHero from "@/assets/city-heroes/chula-vista.jpg";
import santeeHero from "@/assets/city-heroes/santee.jpg";
import laPresaHero from "@/assets/city-heroes/la-presa.jpg";
import lakesideHero from "@/assets/city-heroes/lakeside.jpg";
import bonitaHero from "@/assets/city-heroes/bonita.jpg";
import ranchoSanDiegoHero from "@/assets/city-heroes/rancho-san-diego.jpg";
import jamulHero from "@/assets/city-heroes/jamul.jpg";
import missionValleyHero from "@/assets/city-heroes/mission-valley.jpg";

const cityHeroImages: Record<string, string> = {
  "san-diego": sanDiegoHero,
  "national-city": nationalCityHero,
  "spring-valley": springValleyHero,
  "la-mesa": laMesaHero,
  "el-cajon": elCajonHero,
  "lemon-grove": lemonGroveHero,
  "chula-vista": chulaVistaHero,
  "santee": santeeHero,
  "la-presa": laPresaHero,
  "lakeside": lakesideHero,
  "bonita": bonitaHero,
  "rancho-san-diego": ranchoSanDiegoHero,
  "jamul": jamulHero,
  "mission-valley": missionValleyHero,
};

const sectionImages = [sectionImg1.url, sectionImg2.url, sectionImg3.url];

export const Route = createFileRoute("/areas/california/$city")({
  loader: ({ params }) => {
    const city = caCities.find((c) => c.slug === params.city);
    if (!city) throw notFound();
    return city;
  },
  head: ({ loaderData }) => {
    const name = loaderData?.name ?? "California";
    const slug = loaderData?.slug ?? "";
    const url = `https://mckay.steadyautogrowthdemo.com/areas/california/${slug}`;
    const content = slug ? cityAreaContent[slug] : undefined;
    const description =
      content?.heroDescription ??
      `Local roofing repair, replacement and solar installation in ${name}, California. Licensed, insured and trusted by ${name} homeowners.`;
    return {
      meta: [
        { title: `Roofing & Solar in ${name}, CA – McKay Roofing & Solar` },
        { name: "description", content: description },
        { name: "keywords", content: `roofing ${name}, solar ${name}, roof repair ${name} CA, solar installation ${name}, McKay Roofing & Solar` },
        { property: "og:title", content: `Roofing & Solar in ${name}, CA` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-extrabold">City not found</h1>
      <p className="mt-3 text-muted-foreground">Try our California landing page.</p>
      <Link to="/areas/california" className="mt-6 inline-block rounded-md bg-accent px-5 py-3 text-sm font-bold text-accent-foreground">All California cities</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-extrabold">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: CityPage,
});

function CityPage() {
  const city = Route.useLoaderData();
  const name = city.name;
  const content = cityAreaContent[city.slug];

  const heroTitle = content?.heroTitle ?? `Roofing & Solar in ${name}, CA`;
  const heroDescription = content?.heroDescription ?? city.blurb;
  const heroImage = cityHeroImages[city.slug] ?? areasHero;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={heroImage} alt={`Roofing and solar services in ${name}, California`} />
          <div className="relative mx-auto flex min-h-[85vh] max-w-5xl flex-col justify-center px-4 py-28 sm:px-6 sm:py-36 lg:px-8 lg:py-44">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-foreground/30 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] backdrop-blur">
              <MapPin className="h-3.5 w-3.5" /> {content?.introEyebrow ?? `Serving ${name}`}
            </div>
            <h1 className="mt-6 text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">
              {heroTitle}
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">{heroDescription}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                  <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                  <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
                </Button>
              </div>
            <div className="mt-7 flex items-center gap-1 text-accent">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              <span className="ml-2 text-sm text-primary-foreground/85">150+ five-star reviews • {name}, CA</span>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="relative overflow-hidden">
          <SectionDecor variant="house" side="right" tone="primary" opacity={0.05} />
          <div className="relative mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">What we do in {name}</div>
            <h2 className="mt-3 text-3xl font-extrabold text-primary sm:text-4xl text-balance">
              {content?.introTitle ?? `Trusted roofing and solar for ${name} homeowners`}
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              {content?.introBody ?? `McKay Roofing & Solar has been protecting ${name} homes for over 40 years. From routine roof repairs to full replacements and solar installations paired with new roofs, our local crew handles every project with the same attention to detail.`}
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground/85">
              Most of our {name} calls fall into one of three buckets: a targeted{" "}
              <Link to="/services/roof-repair" className="font-semibold text-primary underline decoration-secondary/40 underline-offset-4 hover:text-secondary">roof repair</Link>{" "}
              for a leak or damaged section, a full{" "}
              <Link to="/services/roof-replacement" className="font-semibold text-primary underline decoration-secondary/40 underline-offset-4 hover:text-secondary">roof replacement</Link>{" "}
              once the roof is past patching, or a{" "}
              <Link to="/services/solar-installation" className="font-semibold text-primary underline decoration-secondary/40 underline-offset-4 hover:text-secondary">solar installation</Link>{" "}
              planned alongside one of those roofing projects so the roof and the array are set up to last together.
            </p>
          </div>
        </section>

        {/* Roofing services */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          <SectionDecor variant="roof" side="left" tone="white" opacity={0.06} />
          <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Roofing Services</div>
              <h2 className="mt-3 text-3xl font-extrabold sm:text-4xl text-balance">Roofing we install and repair in {name}</h2>
              <p className="mt-4 text-primary-foreground/80">
                From single-shingle repairs to full tear-off replacements – done by a licensed Southern California crew that's been on roofs since 1981.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-5">
              {roofingTiles.map((tile) => (
                <ServiceTile
                  key={tile.slug}
                  item={tile}
                  linkTo={{ to: "/areas/california/$city/$service", params: { city: city.slug, service: tile.slug } }}
                />
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-center">
              <p className="text-primary-foreground/85">Need a roof looked at in {name} this week?</p>
              <Button asChild size="sm" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Solar services */}
        <section className="relative overflow-hidden">
          <SectionDecor variant="sun" side="right" tone="primary" opacity={0.06} />
          <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Solar & Storage</div>
              <h2 className="mt-3 text-3xl font-extrabold text-primary sm:text-4xl text-balance">Solar we install in {name}</h2>
              <p className="mt-4 text-muted-foreground">
                Panels, batteries, inverters and EV chargers – designed around your roof, your usage and how long you plan to stay.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
              {solarTiles.map((tile) => (
                <ServiceTile
                  key={tile.slug}
                  item={tile}
                  linkTo={{ to: "/areas/california/$city/$service", params: { city: city.slug, service: tile.slug } }}
                />
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-center">
              <p className="text-muted-foreground">Want to know what solar would actually save you in {name}?</p>
              <Button asChild size="sm" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">Get a Free Solar Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Long-form sections */}
        {content && (
          <section className="bg-muted/40">
            <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
              <div className="space-y-16 lg:space-y-24">
                {content.sections.map((s, i) => {
                  const reverse = i % 2 === 1;
                  const img = sectionImages[i % sectionImages.length];
                  const { extra, bullets, link } = getCitySectionExtra(name, i);
                  return (
                    <article
                      key={s.heading}
                      className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14 ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5">
                        <img
                          src={img}
                          alt={s.heading}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </div>
                      <div>
                        <h3 className="text-2xl font-extrabold text-primary sm:text-3xl text-balance">{s.heading}</h3>
                        <p className="mt-4 text-base leading-relaxed text-foreground/85">{s.body}</p>
                        <p className="mt-4 text-base leading-relaxed text-foreground/75">{extra}</p>
                        <ul className="mt-5 space-y-2">
                          {bullets.map((b) => (
                            <li key={b} className="flex items-start gap-2 text-sm text-foreground/85">
                              <Check className="mt-0.5 h-4 w-4 flex-none text-accent" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                          <Button asChild size="sm" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                            <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                          </Button>
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="btn-lift border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground"
                          >
                            <Link
                              to="/areas/california/$city/$service"
                              params={{ city: city.slug, service: link.slug }}
                            >
                              {link.label} <ArrowRight className="ml-1 h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Nearby cities */}
        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Nearby cities</div>
            <h2 className="mt-2 text-2xl font-extrabold text-primary sm:text-3xl">Other Southern California areas we serve</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {caCities.filter((c) => c.slug !== city.slug).map((c) => (
                <Link
                  key={c.slug}
                  to="/areas/california/$city"
                  params={{ city: c.slug }}
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-secondary hover:text-secondary"
                >
                  <MapPin className="h-3 w-3 text-accent" /> {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTABlock
          title={`Ready for a free ${name} roofing or solar estimate?`}
          subtitle="Tell us about your project. We usually respond same day."
        />
      </main>
      <SiteFooter />
    </div>
  );
}
